import { isDefined, isNotDefined } from '@sgme/fp';
import { type ReactElement, useCallback } from 'react';

import { CreateOrderDropdown } from '@/App/tiles/CashTile/shared/CreateOrderFromCash/OrdersDropdown.tsx';
import { getCurrenciesFromCurrencyPair } from '@/App/tiles/FlexiForward/utils.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import type { OrderType } from '@/store/orders/state.ts';
import { createOrderTileFromCashTile } from '@/store/orders/thunks.ts';
import type { ClientWay } from '@/store/orders/types.ts';
import { selectTileClientId, selectTileProduct } from '@/store/tiles/selectors.ts';
import { selectTilesState } from '@/store/tiles/slice.ts';

export function CreateOrder({ tileId }: { tileId: string }): ReactElement {
  const dispatch = useAppDispatch();

  const currentTile = useAppSelector((state) => selectTilesState(state).entities[tileId]);
  const cashTile = useAppSelector((state) => state.cash[tileId]);
  const clientId = useAppSelector((state) => selectTileClientId(state, tileId));
  const isOverriddenCLientId = useAppSelector((state) => isDefined(selectTileProduct(tileId)(state)?.clientId));

  const handleOrderButton = useCallback(
    (orderType: OrderType, clientWay: ClientWay) => () => {
      if (isNotDefined(currentTile) || isNotDefined(cashTile)) {
        return;
      }

      const currencyPair = cashTile.properties.currencyPair;
      const { currency1, currency2 } = getCurrenciesFromCurrencyPair(currencyPair);
      if (isNotDefined(currency1) || isNotDefined(currency2)) {
        return;
      }

      const clientData = { id: clientId, isOverriddenId: isOverriddenCLientId };
      const notional = {
        amount: cashTile.properties.notional,
        currencyIndex: cashTile.properties.notionalCurrencyIndex,
      };

      dispatch(
        createOrderTileFromCashTile({
          orderType,
          position: { x: currentTile.x + 1, y: currentTile.y },
          currencyPair: { currency1, currency2 },
          clientData,
          clientWay,
          notional,
        }),
      );
    },
    [currentTile, cashTile, clientId, isOverriddenCLientId],
  );

  return (
    <div className="d-flex justify-content-between mt-4px">
      <CreateOrderDropdown clientWay="buy" onClick={handleOrderButton} />
      <CreateOrderDropdown clientWay="sell" onClick={handleOrderButton} />
    </div>
  );
}
